import { useState, useMemo, useEffect } from "react"
import Header from "../../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, InputLabel, TextField, Button } from "@mui/material"

import BackIcon from "../../../../components/BackIcon"
import { useLocation } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import CustomReadableHelperDetail from "../../../../components/CustomReadableHelperDetail"
import CustomFieldsetAccordion from "../../../../components/CustomFieldsetAccordion"
import CustomTextFieldReadable from "../../../../components/CustomTextFieldReadable"
import CustomReadableTable from "../../../../components/CustomReadableTable"
import { Decimal } from "decimal.js"
import normalFormatDate from "../../../utils/date/DDMMYYYFormatDate"
import normalFormatHour from "../../../utils/date/HHMMSSFormatHour"
import CustomTextField from "../../../../components/CustomTextField"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import RateReviewIcon from "@mui/icons-material/RateReview"
import CancelIcon from "@mui/icons-material/Cancel"
import CustomFieldset from "../../../../components/CustomFieldset"
import CustomDatePicker from "../../../../components/CustomDatePicker"
import dayjs from "dayjs"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import Swal from "sweetalert2"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87", // Cambia el color secundario a verde azulado
    },
  },
})

// **********************************************
//               CustomActionButton
// **********************************************
const ActionButtonStyle = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  letterSpacing: "0.03em",
  transition: theme.transitions.create(["all"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[3],
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(1),
    "& svg": {
      fontSize: "1.2rem",
    },
  },
}))
export const CustomActionButton = ({
  actionType = "default",
  label,
  icon,
  color = "primary",
  onAction,
  disabled = false,
  size = "medium",
  ...props
}) => {
  const handleClick = () => {
    if (onAction) {
      onAction(actionType)
    }
  }

  return (
    <ActionButtonStyle
      variant="contained"
      color={color}
      startIcon={icon}
      onClick={handleClick}
      disabled={disabled}
      size={size}
      sx={{
        ...props.sx,
      }}
      {...props}
    >
      {label}
    </ActionButtonStyle>
  )
}

// *************************************************
//             CustomHelperButton
// ************************************************
const CustomHelperButton = ({
  valueInputMain = "",
  valueInputSecondary = "",
  placeholderInputSecondary = "",
  sxInputMain = {},
  sxInputSecondary = {},
  onClick,
  disabledBtn = false,
}) => {
  return (
    <Box display="flex" flexDirection="column">
      <InputLabel>{"\u200B"}</InputLabel>
      <Box display="flex">
        <Button
          variant="contained"
          color="primary"
          disabled={disabledBtn}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...sxInputMain,
          }}
          onClick={onClick}
        >
          {valueInputMain}
        </Button>

        <TextField
          disabled
          fullWidth
          value={valueInputSecondary}
          placeholder={placeholderInputSecondary}
          sx={{
            "& .MuiInputBase-input": {
              overflowX: "auto",
              whiteSpace: "nowrap",
            },
            ...sxInputSecondary,
          }}
        />
      </Box>
    </Box>
  )
}

// *************************************************
//             ContainerDatosGenerales
// ************************************************
const ContainerDatosGenerales = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "repeat(8, auto)",
  gridTemplateAreas: `
    "IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra IOrdenCompra"
    "IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion IDescripcion"
    "IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor IProveedor"
    "IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion"
    "IFecEmision IFecEmision IFecEmision IFecEmision IFecEmision IFecEmision ISubtotal ISubtotal ISubtotal ISubtotal ISubtotal ISubtotal"
    "IEstadoOc IEstadoOc IEstadoOc IEstadoOc IEstadoOc IEstadoOc IDescuento IDescuento IDescuento IDescuento IDescuento IDescuento"
    "IContrato IContrato IContrato IContrato IContrato IContrato ITotal ITotal ITotal ITotal ITotal ITotal"
    "IImportacion IImportacion IImportacion IImportacion IImportacion IImportacion IInventarios IInventarios IInventarios IInventarios IInventarios IInventarios"
  `,
  gap: "8px",
  alignItems: "center",
  // Versión para pantallas pequeñas
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "repeat(12, auto)",
    gridTemplateAreas: `
      "IOrdenCompra"
      "IDescripcion"
      "IProveedor"
      "IObservacion"
      "IFecEmision"
      "ISubtotal"
      "IEstadoOc"
      "IDescuento"
      "IContrato"
      "ITotal"
      "IImportacion"
      "IInventarios"
    `,
    gap: "12px",
  },
}))

const IOrdenCompra = styled(Box)({
  gridArea: "IOrdenCompra",
})
const IImportacion = styled(Box)({
  gridArea: "IImportacion",
})

const IDescripcion = styled(Box)({
  gridArea: "IDescripcion",
})

const IProveedor = styled(Box)({
  gridArea: "IProveedor",
})

const IObservacion = styled(Box)({
  gridArea: "IObservacion",
})

const IFecEmision = styled(Box)({
  gridArea: "IFecEmision",
})

const ISubtotal = styled(Box)({
  gridArea: "ISubtotal",
})

const IEstadoOc = styled(Box)({
  gridArea: "IEstadoOc",
})

const IDescuento = styled(Box)({
  gridArea: "IDescuento",
})

const IContrato = styled(Box)({
  gridArea: "IContrato",
})

const ITotal = styled(Box)({
  gridArea: "ITotal",
})

const IInventarios = styled(Box)({
  gridArea: "IInventarios",
})

// *************************************************
//             ContainerAcciones
// ************************************************

const ContainerAcciones = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
    "IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion"
    "IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso"
    "IAprobarBtn IAprobarBtn IAprobarBtn IAprobarBtn IRevDesBtn IRevDesBtn IRevDesBtn IRevDesBtn IRevDesBtn IAnularBtn IAnularBtn IAnularBtn"
  `,
  gap: "8px",
  alignItems: "center",

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "repeat(5, auto)",
    gridTemplateAreas: `
      "IObservacion"
      "IAprobarBtn"
      "IRevDesBtn"
      "IAnularBtn"
    `,
  },
}))

const IObservacionF = styled(Box)({
  gridArea: "IObservacion",
})

const IAprobarBtn = styled(Box)({
  gridArea: "IAprobarBtn",
})

const IRevDesBtn = styled(Box)({
  gridArea: "IRevDesBtn",
})

const IAnularBtn = styled(Box)({
  gridArea: "IAnularBtn",
})

// *************************************************
//             ContainerIObservacionF
// ************************************************
const ContainerIObservacionF = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
    "IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion IObservacion"
    "IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso IFecProceso"
    "IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn IObservacionBtn"
  `,
  gap: "8px",
  alignItems: "center",

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "repeat(3, auto)",
    gridTemplateAreas: `
      "IObservacion"
      "IFecProceso"
      "IObservacionBtn"
    `,
  },
}))
const IObservacionAcc = styled(Box)({
  gridArea: "IObservacion",
})
const IFecProceso = styled(Box)({
  gridArea: "IFecProceso",
})
const IObservacionBtn = styled(Box)({
  gridArea: "IObservacionBtn",
})

const OCGerenciaAdquisicionesCrearOrdenCompra = () => {
  const location = useLocation()
  const [expandedDatosGenerales, setExpandedDatosGenerales] = useState(false)
  const handleToggleDatosGenerales = () => {
    setExpandedDatosGenerales((prev) => !prev)
  }
  const [expandedCentrosCostos, setExpandedCentrosCostos] = useState(false)
  const handleToggleCentrosCostos = () => {
    setExpandedCentrosCostos((prev) => !prev)
  }
  const [expandedCondicionesPago, setExpandedCondicionesPago] = useState(false)
  const handleToggleCondicionesPago = () => {
    setExpandedCondicionesPago((prev) => !prev)
  }
  const [expandedDetalleOrdenCompra, setExpandedDetalleOrdenCompra] = useState(false)
  const handleToggleDetalleOrdenCompra = () => {
    setExpandedDetalleOrdenCompra((prev) => !prev)
  }
  const [expandedObservaciones, setExpandedObservaciones] = useState(false)
  const handleToggleObservaciones = () => {
    setExpandedObservaciones((prev) => !prev)
  }
  const {
    data: dataOrdenCompra = {},
    isError: dataErrorOrdenCompra,
    isFetching: isFetchingOrdenCompra,
    isLoading: isLoadingOrdenCompras,
    refetch: refetchOrdenCompra,
  } = useGetOrdenCompra()

  const { cabeceraOC, controlesUIOC, centrosCostosOC, detalleOrdenOC, condicionesPagoOC, observacionesOC } = useMemo(
    () => ({
      cabeceraOC: dataOrdenCompra.cabecera || {},
      controlesUIOC: dataOrdenCompra.controlesUI || {},
      centrosCostosOC: dataOrdenCompra.centrosCostos || [],
      detalleOrdenOC: dataOrdenCompra.detalleOrden || [],
      condicionesPagoOC: dataOrdenCompra.condicionesPago || [],
      observacionesOC: dataOrdenCompra.observaciones || [],
    }),
    [dataOrdenCompra],
  )

  function useGetOrdenCompra() {
    return useQuery({
      queryKey: ["ordenCompraOCGerenciaAdquisicionesCrearOrdenCompra"],
      queryFn: async () => {
        const response = await fetchwrapper("/OCGerenciaAdquisiciones/getSpecificOrdenCompra", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ordenCompra: location.state?.cocid,
          }),
        })
        const result = await response.json()
        return result.data
      },
      keepPreviousData: false,
      retry: 1,
    })
  }

  // Estados para las acciones
  const [observacionAccion, setObservacionAccion] = useState("")
  const [dtpValueFront, setDtpValueFront] = useState(dayjs(null))

  useEffect(() => {
    setDtpValueFront(dayjs(cabeceraOC?.dtpFechaValue, "YYYY-MM-DD HH:mm:ss").utcOffset(-5))
  }, [cabeceraOC?.dtpFechaValue])

  const { mutateAsync: saveOrdenCompraAccion, isPending: isSavingOrdenCompraAccion } = useSaveOrdenCompraAccion()

  function useSaveOrdenCompraAccion() {
    return useMutation({
      mutationFn: async (data) => {
        const options = {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }

        try {
          const response = await fetchwrapper(`/OCGerenciaAdquisiciones/saveVerificacionSpecificOrdenCompra`, options)
          const result = await response.json()
          return result?.data
        } catch (errorResponse) {
          throw errorResponse?.details?.msg || "Error al ejecutar la accion"
        }
      },
    })
  }
  // Funcion que se ejecuta cuando se comenta/aprueba/revisa/niega
  const handleSaveOrdenCompraAccion = async (tipoAccion) => {
    const data = {
      ordenCompra: cabeceraOC.txtCocId,
      tipoAccion,
      extras: {
        bEliMaeArt: false,
      },
      cabecera: {
        ...cabeceraOC,
        txtObserva: observacionAccion,
        dtpFecha: dtpValueFront.format("YYYY-MM-DD HH:mm:ss"), // Si el usuario decide cambiar fecha del proceso
        txtCodProTag: dayjs(cabeceraOC?.txtCodProTag, "YYYY-MM-DD HH:mm:ss").utcOffset(-5), // Forzar offset de Ecuador,
      },
    }
    try {
      const response = await saveOrdenCompraAccion(data)
      const msgExito = response?.msg || "Acción realizada con éxito"

      Swal.fire({
        title: "Éxito",
        text: msgExito,
        icon: "success",
        confirmButtonText: "OK",
      }).then(() => {
        refetchOrdenCompra() // Refetch todo la orden de compra, esto traera la nueva observacion agregado
      })
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err,
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }
  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Aprobación de Orden de Compra Gerencia Adquisiciones</b>
        </div>
        <CustomBackdrop isLoading={isSavingOrdenCompraAccion} />

        <Box className={StyledRoot}>
          {/* *********************************************
                  COMENTARIO / APROBACION / NEGACION
          ******************************************** */}
          <IObservacionF>
            <CustomFieldset title={`Observación`} sx={{ marginBottom: "15px" }}>
              <ContainerIObservacionF>
                <IObservacionAcc>
                  <CustomTextField
                    label="Observación"
                    value={observacionAccion}
                    setValue={(v) => setObservacionAccion(v)}
                  />
                </IObservacionAcc>
                <IFecProceso>
                  <Box display="flex" flexDirection="column">
                    <InputLabel>Fecha Proceso</InputLabel>
                    <Box display="flex">
                      <div
                        style={{
                          display: "inline-flex",
                          marginRight: "10px",
                        }}
                      >
                        <CustomDatePicker
                          value={dtpValueFront}
                          setValue={(v) => setDtpValueFront(v)}
                          sx={{
                            "& .MuiInputBase-input": {
                              overflowX: "auto",
                              whiteSpace: "nowrap",
                            },
                          }}
                          disabled={!controlesUIOC?.dtpFecha}
                        />
                      </div>
                      <TextField
                        sx={{
                          "& .MuiInputBase-input": {
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                          },
                          width: "100%",
                        }}
                        value={cabeceraOC?.txtPuerto}
                        disabled
                      />
                    </Box>
                  </Box>
                  <CustomReadableHelperDetail
                    label="Fecha Proceso"
                    valueInputMain={cabeceraOC?.txtFecEmi}
                    valueInputSecondary={cabeceraOC?.txtPuerto}
                    sxInputMain={{
                      minWidth: "170px",
                      maxWidth: "170px",
                      marginRight: "10px",
                    }}
                    sxInputSecondary={{
                      width: "100%",
                    }}
                  />
                </IFecProceso>
                <IObservacionBtn>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={(_) => handleSaveOrdenCompraAccion("O")}
                  >
                    Observación
                  </Button>
                </IObservacionBtn>
              </ContainerIObservacionF>
              <ContainerAcciones>
                <IAprobarBtn>
                  <CustomActionButton
                    actionType="A"
                    label="Aprobar"
                    icon={<CheckCircleOutlineIcon />}
                    color="success"
                    onAction={(type) => handleSaveOrdenCompraAccion(type)}
                    fullWidth
                    disabled={!controlesUIOC?.cmdApro}
                  />
                </IAprobarBtn>
                <IRevDesBtn>
                  <CustomActionButton
                    actionType="D"
                    label="Revisar/Desaprobar"
                    icon={<RateReviewIcon />}
                    color="warning"
                    onAction={(type) => handleSaveOrdenCompraAccion(type)}
                    fullWidth
                    disabled={!controlesUIOC?.cmdRevisa}
                  />
                </IRevDesBtn>
                <IAnularBtn>
                  <CustomActionButton
                    actionType="N"
                    label="Anular"
                    icon={<CancelIcon />}
                    color="error"
                    onAction={(type) => handleSaveOrdenCompraAccion(type)}
                    fullWidth
                    disabled={!controlesUIOC?.cmdNoApro}
                  />
                </IAnularBtn>
              </ContainerAcciones>
            </CustomFieldset>
          </IObservacionF>

          {/* *********************************************
                          DATOS GENERALES
          ******************************************** */}
          <CustomFieldsetAccordion
            title="Datos Generales"
            expanded={expandedDatosGenerales}
            onToggle={handleToggleDatosGenerales}
          >
            <ContainerDatosGenerales>
              <IOrdenCompra>
                <CustomReadableHelperDetail
                  label="Orden/Compra"
                  valueInputMain={cabeceraOC?.txtCocId}
                  valueInputSecondary={cabeceraOC?.txtTipo}
                  sxInputMain={{
                    minWidth: "170px",
                    maxWidth: "170px",
                    marginRight: "10px",
                  }}
                  sxInputSecondary={{
                    width: "100%",
                  }}
                />
              </IOrdenCompra>
              <IDescripcion>
                <CustomTextFieldReadable label="Descripción" value={cabeceraOC?.txtDescri} />
              </IDescripcion>
              <IProveedor>
                <CustomReadableHelperDetail
                  label="Proveedor"
                  valueInputMain={cabeceraOC?.txtCodPro}
                  valueInputSecondary={cabeceraOC?.txtNomPro}
                  sxInputMain={{
                    minWidth: "170px",
                    maxWidth: "170px",
                    marginRight: "10px",
                  }}
                  sxInputSecondary={{
                    width: "100%",
                  }}
                />
              </IProveedor>
              <IObservacion>
                <CustomTextFieldReadable label="Observación" value={cabeceraOC?.txtObs} />
              </IObservacion>
              <IFecEmision>
                <CustomTextFieldReadable label="Fecha Emisión" value={cabeceraOC?.txtFecEmi} />
              </IFecEmision>
              <ISubtotal>
                <CustomTextFieldReadable label="Subtotal" value={cabeceraOC?.txtSubTot} />
              </ISubtotal>
              <IEstadoOc>
                <CustomTextFieldReadable label="Estado" value={cabeceraOC?.txtEstado} />
              </IEstadoOc>
              <IDescuento>
                <CustomTextFieldReadable label="Descuento" value={cabeceraOC?.txtDesc} />
              </IDescuento>
              <IContrato>
                <CustomHelperButton
                  valueInputMain="Contrato"
                  disabledBtn={!controlesUIOC?.cmdContrato}
                  sxInputMain={{
                    minWidth: "170px",
                    maxWidth: "170px",
                    marginRight: "10px",
                  }}
                  sxInputSecondary={{
                    width: "100%",
                  }}
                />
              </IContrato>
              <ITotal>
                <CustomTextFieldReadable label="Total" value={cabeceraOC?.txtTOTAL} />
              </ITotal>
              <IImportacion>
                <CustomHelperButton
                  valueInputMain="Importación"
                  disabledBtn={!controlesUIOC?.cmdImporta}
                  sxInputMain={{
                    minWidth: "170px",
                    maxWidth: "170px",
                    marginRight: "10px",
                  }}
                  sxInputSecondary={{
                    width: "100%",
                  }}
                />
              </IImportacion>
              <IInventarios>
                <CustomHelperButton
                  valueInputMain="Inventarios"
                  valueInputSecondary={cabeceraOC?.txtStaInv}
                  disabledBtn={!controlesUIOC?.cmdInventarios}
                  sxInputMain={{
                    minWidth: "170px",
                    maxWidth: "170px",
                    marginRight: "10px",
                  }}
                  sxInputSecondary={{
                    width: "100%",
                  }}
                />
              </IInventarios>
            </ContainerDatosGenerales>
          </CustomFieldsetAccordion>
          {/* *********************************************
                          CENTRO DE COSTOS
          ******************************************** */}
          <CustomFieldsetAccordion
            title="Centro de Costos"
            expanded={expandedCentrosCostos}
            onToggle={handleToggleCentrosCostos}
          >
            <CustomReadableTable
              syncURL={false}
              data={centrosCostosOC}
              isLoading={isLoadingOrdenCompras || isFetchingOrdenCompra}
              columnsTable={[
                {
                  accessorKey: "pctacodigo",
                  header: "Cuenta",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "pctanomcta",
                  header: "Nombre Cuenta",
                  size: 250,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "coscodigo",
                  header: "CenCosto",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "cosdescri",
                  header: "Centro de Costo",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "parcodigo",
                  header: "Partida",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "parnompar",
                  header: " Nombre de Partido",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "parnivel",
                  header: "Saldo Partido",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "asiporcentaje",
                  header: "Porcentaje",
                  size: 130,
                  Cell: ({ cell }) => {
                    return <span>{cell.getValue()}%</span>
                  },
                },
                {
                  accessorKey: "asivalor",
                  header: "Valor",
                  size: 130,
                  Cell: ({ cell }) => {
                    return <span>${cell.getValue()}</span>
                  },
                },
              ]}
              isError={dataErrorOrdenCompra}
              errorMsgFilterSearch="No se pudo encontrar la información"
              refetch={refetchOrdenCompra}
            />
          </CustomFieldsetAccordion>

          {/* *********************************************
                          CONDICIONES DE PAGO
          ******************************************** */}
          <CustomFieldsetAccordion
            title="Condiciones de Pago"
            expanded={expandedCondicionesPago}
            onToggle={handleToggleCondicionesPago}
          >
            <CustomReadableTable
              syncURL={false}
              data={condicionesPagoOC}
              isLoading={isLoadingOrdenCompras || isFetchingOrdenCompra}
              columnsTable={[
                {
                  accessorKey: "fpcodigo",
                  header: "Código",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "fpdescrip",
                  header: "Descripción",
                  size: 150,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "fpocfecven",
                  header: "Fecha Vencimiento",
                  size: 200,
                  Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
                },
                {
                  accessorKey: "fpocvalor",
                  header: "Valor",
                  size: 150,
                  Cell: ({ cell }) => {
                    return <span>${cell.getValue()}</span>
                  },
                },
              ]}
              isError={dataErrorOrdenCompra}
              errorMsgFilterSearch="No se pudo encontrar la información"
              refetch={refetchOrdenCompra}
            />
          </CustomFieldsetAccordion>

          {/* *********************************************
                          DETALLE ORDEN COMPRA
          ******************************************** */}
          <CustomFieldsetAccordion
            title="Detalle Orden de Compra"
            expanded={expandedDetalleOrdenCompra}
            onToggle={handleToggleDetalleOrdenCompra}
          >
            <CustomReadableTable
              syncURL={false}
              data={detalleOrdenOC}
              isLoading={isLoadingOrdenCompras || isFetchingOrdenCompra}
              columnsTable={[
                {
                  accessorKey: "artcodigo",
                  header: "Código",
                  size: 100,
                  Cell: ({ cell }) => {
                    const value = cell.getValue()
                    return <span>{value}</span>
                  },
                },
                {
                  accessorKey: "artdescri",
                  header: "Descripción",
                  size: 300,
                  Cell: ({ cell }) => {
                    const value = cell.getValue()
                    return <span>{value}</span>
                  },
                },
                {
                  accessorKey: "toccantidad",
                  header: "Cantidad",
                  size: 120,
                  Cell: ({ cell }) => {
                    const value = new Decimal(cell.getValue())
                    const numericValue = value.toNumber()
                    const formatted = numericValue.toLocaleString?.("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    return <span>{formatted}</span>
                  },
                },
                {
                  accessorKey: "tocvaloruni",
                  header: "Costo Unitario",
                  size: 120,
                  Cell: ({ cell }) => {
                    return <span>${cell.getValue()}</span>
                  },
                },
                {
                  accessorKey: "tocvalordsct",
                  header: "Descuento",
                  size: 120,
                  Cell: ({ cell }) => {
                    return <span>${cell.getValue()}</span>
                  },
                },
                {
                  accessorKey: "tocvalornet",
                  header: "Total",
                  size: 140,
                  Cell: ({ cell }) => {
                    const value = new Decimal(cell.getValue())
                    const numericValue = value.toNumber()
                    const formatted = numericValue.toLocaleString?.("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    return <span>{formatted}</span>
                  },
                },
              ]}
              isError={dataErrorOrdenCompra}
              errorMsgFilterSearch="No se pudo encontrar la información"
              refetch={refetchOrdenCompra}
            />
          </CustomFieldsetAccordion>

          {/* *********************************************
                          OBSERVACIONES
          ******************************************** */}
          <CustomFieldsetAccordion
            title="Observaciones"
            expanded={expandedObservaciones}
            onToggle={handleToggleObservaciones}
          >
            <CustomReadableTable
              syncURL={false}
              data={observacionesOC}
              isLoading={isLoadingOrdenCompras || isFetchingOrdenCompra}
              columnsTable={[
                {
                  accessorKey: "fecha",
                  header: "Fecha",
                  size: 120,
                  Cell: ({ cell }) => {
                    const value = cell.getValue()
                    return <span>{normalFormatDate(value)}</span>
                  },
                },
                {
                  accessorKey: "hora",
                  header: "Hora",
                  size: 100,
                  Cell: ({ cell }) => {
                    const value = cell.getValue()
                    return <span>{normalFormatHour(value)}</span>
                  },
                },
                {
                  accessorKey: "accion",
                  header: "Acción",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "modulo",
                  header: "Revisión",
                  size: 80,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "observacion",
                  header: "Observación",
                  size: 500,
                  Cell: ({ cell }) => (
                    <span
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {cell.getValue()}
                    </span>
                  ),
                },
                {
                  accessorKey: "estado",
                  header: "Estado",
                  size: 120,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "documentoRelacionado",
                  header: "Importación",
                  size: 180,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "usuario",
                  header: "Usuario",
                  size: 500,
                  Cell: ({ cell }) => <span>{cell.getValue()}</span>,
                },
              ]}
              isError={dataErrorOrdenCompra}
              errorMsgFilterSearch="No se pudo encontrar la información"
              refetch={refetchOrdenCompra}
            />
          </CustomFieldsetAccordion>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default OCGerenciaAdquisicionesCrearOrdenCompra
